import { Vec2 } from '../utils/math';
import { TracedSegment } from '../engine/Ray';
import { FieldGrid } from './FieldGrid';
import { FieldGridManager } from './FieldGridManager';
import { FDTDSolver } from './FDTDSolver';
import { SpatialHashGrid } from '../spatial/SpatialHashGrid';
import { ConvergenceDetector } from '../spatial/ConvergenceDetector';
import { wavelengthToChannel } from '../utils/color';

const FDTD_STEPS_PER_FRAME = 10; // Number of FDTD iterations per render frame

/**
 * Hybrid renderer that combines geometric ray tracing with FDTD field simulation.
 *
 * Flow:
 * 1. Ray tracer produces traced segments (geometric optics)
 * 2. SpatialHashGrid indexes segments, ConvergenceDetector finds interference zones
 * 3. For each interference zone, a FieldGrid is allocated
 * 4. Rays entering a zone are injected as field sources
 * 5. FDTD solver updates the field for several timesteps
 * 6. Field intensity is made available for rendering
 */
export class HybridRenderer {
  private spatialGrid: SpatialHashGrid;
  private convergenceDetector: ConvergenceDetector;
  private fieldManager: FieldGridManager;
  private fdtdSolver: FDTDSolver;
  private enabled: boolean = true;
  private simulationTime: number = 0;
  private cachedSegments: TracedSegment[] | null = null;

  constructor() {
    this.spatialGrid = new SpatialHashGrid(80); // 80px cells
    this.convergenceDetector = new ConvergenceDetector(0.5);
    this.fieldManager = new FieldGridManager();
    this.fdtdSolver = new FDTDSolver();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Detect interference regions, allocate/release field grids, inject rays.
   * Called when the scene changes (not every frame).
   */
  update(segments: TracedSegment[]): FieldGrid[] {
    if (!this.enabled || segments.length === 0) {
      this.fieldManager.retainOnly(new Set());
      return [];
    }

    // 1. Index segments into spatial grid
    this.spatialGrid.indexSegments(segments);

    // 2. Detect interference regions
    const regions = this.convergenceDetector.detect(this.spatialGrid);

    // 3. Manage field grids (allocate/release)
    const activeKeys = new Set<string>();
    const activeGrids: FieldGrid[] = [];

    for (const region of regions) {
      activeKeys.add(region.cellKey);
      const grid = this.fieldManager.getOrAllocate(region.cellKey, region.bounds, region.score);
      if (grid) {
        activeGrids.push(grid);
      }
    }

    this.fieldManager.retainOnly(activeKeys);

    // 4. Inject rays into field grids
    this.cachedSegments = segments;
    for (const grid of activeGrids) {
      this.injectRaysToField(grid, segments);
    }

    return activeGrids;
  }

  /**
   * Advance FDTD simulation by a few steps. Called every frame when active.
   * Time-budgeted to avoid locking the main thread.
   */
  stepFDTD(grids: FieldGrid[]): void {
    for (const grid of grids) {
      // Inject continuous source each step
      if (this.cachedSegments) {
        this.injectRaysToField(grid, this.cachedSegments);
      }

      const dt = FDTDSolver.courantTimestep(grid.cellSize * 1e-6);
      for (let i = 0; i < FDTD_STEPS_PER_FRAME; i++) {
        this.fdtdSolver.update(grid, dt);
        this.simulationTime += dt;
      }
      grid.updateIntensity();
    }
  }

  /**
   * Inject traced ray segments that pass through a field grid as EM sources.
   */
  private injectRaysToField(grid: FieldGrid, segments: TracedSegment[]): void {
    for (const seg of segments) {
      // Check if this segment passes through the grid bounds
      if (!this.segmentIntersectsGrid(seg, grid)) continue;

      // Find entry point into grid
      const entry = this.clipSegmentToGrid(seg, grid);
      if (!entry) continue;

      const { gx, gy } = grid.worldToGrid(entry.x, entry.y);

      // Map wavelength to channel
      const channel = wavelengthToChannel(seg.wavelength);
      if (channel >= grid.wavelengthCount) continue; // Channel beyond grid capacity

      const dir = seg.end.sub(seg.start).normalize();
      const amplitude = seg.intensity * 0.1; // Scale factor for field injection

      // Use tracked phase from ray tracing + time evolution
      const wl = seg.wavelength;
      const omega = 2 * Math.PI * 3e8 / wl; // angular frequency
      const phase = (seg.phase ?? 0) + this.simulationTime * omega;

      this.fdtdSolver.injectBeamSource(
        grid, gx, gy,
        dir.x, dir.y,
        channel,
        amplitude,
        phase,
        5, // beam width in grid cells
      );
    }
  }

  private segmentIntersectsGrid(seg: TracedSegment, grid: FieldGrid): boolean {
    const b = grid.bounds;
    // Quick AABB check
    const minX = Math.min(seg.start.x, seg.end.x);
    const maxX = Math.max(seg.start.x, seg.end.x);
    const minY = Math.min(seg.start.y, seg.end.y);
    const maxY = Math.max(seg.start.y, seg.end.y);

    return !(maxX < b.x || minX > b.x + b.width || maxY < b.y || minY > b.y + b.height);
  }

  private clipSegmentToGrid(seg: TracedSegment, grid: FieldGrid): Vec2 | null {
    const b = grid.bounds;

    // If start is inside, use it
    if (seg.start.x >= b.x && seg.start.x <= b.x + b.width &&
        seg.start.y >= b.y && seg.start.y <= b.y + b.height) {
      return seg.start;
    }

    // Otherwise find intersection with grid boundary
    const dir = seg.end.sub(seg.start);
    const len = dir.length();
    if (len < 1e-6) return null;

    // Simple clamp to bounds
    const t0x = (b.x - seg.start.x) / dir.x;
    const t1x = (b.x + b.width - seg.start.x) / dir.x;
    const t0y = (b.y - seg.start.y) / dir.y;
    const t1y = (b.y + b.height - seg.start.y) / dir.y;

    const tmin = Math.max(
      Math.min(isFinite(t0x) ? t0x : -Infinity, isFinite(t1x) ? t1x : -Infinity),
      Math.min(isFinite(t0y) ? t0y : -Infinity, isFinite(t1y) ? t1y : -Infinity),
      0,
    );
    const tmax = Math.min(
      Math.max(isFinite(t0x) ? t0x : Infinity, isFinite(t1x) ? t1x : Infinity),
      Math.max(isFinite(t0y) ? t0y : Infinity, isFinite(t1y) ? t1y : Infinity),
      1,
    );

    if (tmin > tmax) return null;

    return seg.start.add(dir.scale(tmin));
  }

  getFieldManager(): FieldGridManager {
    return this.fieldManager;
  }

  getSpatialGrid(): SpatialHashGrid {
    return this.spatialGrid;
  }
}
